import { supabase } from '@/integrations/supabase/client';

/**
 * Enhanced session recovery utility for handling idle timeouts
 */
export class SessionRecovery {
  private static instance: SessionRecovery;
  private retryAttempts = 0;
  private maxRetries = 3;
  private retryDelay = 1000;
  private isRecovering = false;

  static getInstance(): SessionRecovery {
    if (!SessionRecovery.instance) {
      SessionRecovery.instance = new SessionRecovery();
    }
    return SessionRecovery.instance;
  }

  /**
   * Attempt to recover session after idle period
   */
  async recoverSession(): Promise<boolean> {
    if (this.isRecovering) {
      console.log('Session recovery already in progress');
      return false;
    }

    this.isRecovering = true;
    console.log('Starting session recovery...');

    try {
      // First, try to refresh the session
      const { data, error } = await supabase.auth.refreshSession();
      
      if (!error && data?.session) {
        console.log('Session recovered successfully');
        this.resetRetryState();
        return true;
      }

      // If refresh failed, try to get the current session
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      
      if (!sessionError && sessionData?.session) {
        console.log('Session is still valid');
        this.resetRetryState();
        return true;
      }

      // If both failed, attempt retry with exponential backoff
      if (this.retryAttempts < this.maxRetries) {
        this.retryAttempts++;
        const delay = this.retryDelay * Math.pow(2, this.retryAttempts - 1);
        
        console.log(`Session recovery failed, retrying in ${delay}ms (attempt ${this.retryAttempts}/${this.maxRetries})`);
        
        await new Promise(resolve => setTimeout(resolve, delay));
        this.isRecovering = false;
        return this.recoverSession();
      }

      console.warn('Session recovery failed after all retries');
      this.resetRetryState();
      return false;

    } catch (error) {
      console.error('Session recovery error:', error);
      this.resetRetryState();
      return false;
    } finally {
      this.isRecovering = false;
    }
  }

  /**
   * Check if session is valid and refresh if needed
   */
  async checkAndRefreshSession(): Promise<boolean> {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error) {
        console.warn('Session check error:', error);
        return this.recoverSession();
      }

      if (!session) {
        console.log('No active session found');
        return false;
      }

      // Check if session is close to expiring (within 5 minutes)
      const expiresAt = session.expires_at;
      const now = Math.floor(Date.now() / 1000);
      const fiveMinutes = 5 * 60;

      if (expiresAt && (expiresAt - now) < fiveMinutes) {
        console.log('Session expires soon, refreshing...');
        return this.recoverSession();
      }

      return true;
    } catch (error) {
      console.error('Error checking session:', error);
      return false;
    }
  }

  private resetRetryState(): void {
    this.retryAttempts = 0;
    this.isRecovering = false;
  }

  /**
   * Initialize session recovery listeners
   */
  initialize(): void {
    // Listen for auth state changes
    supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'TOKEN_REFRESHED') {
        console.log('Auth token refreshed successfully');
        this.resetRetryState();
      } else if (event === 'SIGNED_OUT') {
        console.log('User signed out');
        this.resetRetryState();
      }
    });

    // Check session periodically when app becomes visible
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        this.checkAndRefreshSession().catch(error => {
          console.error('Session check on visibility change failed:', error);
        });
      }
    });

    // Check session when app gains focus
    window.addEventListener('focus', () => {
      this.checkAndRefreshSession().catch(error => {
        console.error('Session check on focus failed:', error);
      });
    });
  }
}

export const sessionRecovery = SessionRecovery.getInstance();